import {
  DEFAULT_DISPLAY_MODE,
  DisplayMode,
  getDisplayModePreferenceKey,
  normalizeDisplayMode,
} from "@/lib/display-mode";
import {
  persistBoPreference,
  readBoPreference,
} from "@/lib/user-preferences/store";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type DisplayModeContextValue = {
  /** Current display mode (defaults to {@link DEFAULT_DISPLAY_MODE}). */
  mode: DisplayMode;
  /** Persists a new mode for this form (per-user, per sheet type). */
  setMode: (mode: DisplayMode) => void;
  /** Convenience flag: true when `mode === "essentiel"`. */
  isSimplified: boolean;
  /** The form this mode belongs to (undefined when no provider is mounted). */
  formKey?: string;
  /** True when a real provider is mounted (a formKey drives the mode). */
  available: boolean;
};

const noop = () => {};

const DisplayModeContext = createContext<DisplayModeContextValue>({
  mode: DEFAULT_DISPLAY_MODE,
  setMode: noop,
  isSimplified: DEFAULT_DISPLAY_MODE === "essentiel",
  available: false,
});

/**
 * Reads the display mode for a form and exposes a persisting setter. The state
 * is initialised from the hydrated `bo_preference` cache and re-read whenever
 * the form (preference key) changes. Callers that own the sheet layout use this
 * directly so they can both read the mode and feed the context provider.
 */
export function useDisplayModeState(
  formKey?: string,
  /**
   * Instance-level default applied ONLY when the user has no saved preference
   * for this form (e.g. `"essentiel"` for new instances). When omitted the
   * product-wide {@link DEFAULT_DISPLAY_MODE} is used as before.
   */
  instanceDefaultMode?: DisplayMode,
): {
  mode: DisplayMode;
  setMode: (mode: DisplayMode) => void;
} {
  const preferenceKey = formKey
    ? getDisplayModePreferenceKey(formKey)
    : undefined;

  // Resolve the mode: an explicit user preference always wins; otherwise fall
  // back to the instance default (if any), then the product-wide default.
  // We check for `undefined` explicitly so a stored value is distinguished from
  // "no preference yet" (where the instance default should apply).
  const resolveMode = (): DisplayMode => {
    const stored = preferenceKey
      ? readBoPreference<DisplayMode>(preferenceKey)
      : undefined;
    if (stored !== undefined) return normalizeDisplayMode(stored);
    return instanceDefaultMode ?? DEFAULT_DISPLAY_MODE;
  };

  const [mode, setModeState] = useState<DisplayMode>(resolveMode);

  useEffect(() => {
    setModeState(resolveMode());
    // `instanceDefaultMode` may arrive asynchronously (instance data load), so
    // re-resolve when it changes — but only "no preference" cases are affected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceKey, instanceDefaultMode]);

  const setMode = useCallback(
    (next: DisplayMode) => {
      setModeState(next);
      if (preferenceKey) persistBoPreference(preferenceKey, next);
    },
    [preferenceKey],
  );

  return { mode, setMode };
}

/** Provides the display-mode value to descendants (card, header, fields). */
export function DisplayModeContextProvider({
  mode,
  setMode,
  formKey,
  children,
}: {
  mode: DisplayMode;
  setMode: (mode: DisplayMode) => void;
  formKey?: string;
  children: ReactNode;
}) {
  const value = useMemo<DisplayModeContextValue>(
    () => ({
      mode,
      setMode,
      isSimplified: mode === "essentiel",
      formKey,
      available: Boolean(formKey),
    }),
    [mode, setMode, formKey],
  );

  return (
    <DisplayModeContext.Provider value={value}>
      {children}
    </DisplayModeContext.Provider>
  );
}

/**
 * Self-contained provider (reads + persists on its own). Handy for sheets that
 * do not need to read the mode at their own level. Sheets that switch their
 * body on the mode should instead call {@link useDisplayModeState} and feed
 * {@link DisplayModeContextProvider} so they can read the value too.
 */
export function DisplayModeProvider({
  formKey,
  children,
}: {
  formKey?: string;
  children: ReactNode;
}) {
  const { mode, setMode } = useDisplayModeState(formKey);
  return (
    <DisplayModeContextProvider mode={mode} setMode={setMode} formKey={formKey}>
      {children}
    </DisplayModeContextProvider>
  );
}

export function useDisplayMode() {
  return useContext(DisplayModeContext);
}
